import { alertText as say } from './copy';

/**
 * What the shared steps say.
 *
 * Every sentence here belongs to a step rather than to a door: a code that does not
 * match, a sign-in the server turned down, a post that failed. The step is the same
 * wherever it runs, so the sentence is too, and changing one changes it everywhere.
 *
 * What a door says about its own outcome lives in doors/<door>/messages.js, one file
 * per door, so a chapter and an article can word the same moment differently.
 *
 * `show` is exported for those files: it is the Swal plumbing, and nothing else.
 */

/**
 * Swal comes from the layout rather than this bundle, so a page without it has to
 * finish the job silently rather than throw. `whenMissing` is how a caller keeps
 * whatever had to happen after the box was dismissed.
 */
export function show(options) {
    if (typeof Swal === 'undefined') {
        if (typeof options.whenMissing === 'function') {
            options.whenMissing();
        }

        return;
    }

    const { whenMissing, onConfirm, ...swal } = options;

    Swal.fire(swal).then((result) => {
        if (result.isConfirmed && typeof onConfirm === 'function') {
            onConfirm();
        }
    });
}

/** Support addresses arrive as links, and Swal only renders those through html. */
function body(message) {
    return message && message.indexOf('<a') !== -1
        ? { html: message }
        : { text: message || '' };
}

/** The six digits typed on the confirmation screen do not match. */
export function invalidCode() {
    show({
        title: say('invalid_code', 'Invalid Code'),
        text: say('invalid_code_text', 'Please make sure you entered the complete 6-digit code.'),
        icon: 'warning',
        confirmButtonText: say('try_again', 'Try Again')
    });
}

/** The emailed sign-in code does not match, or has expired. */
export function wrongLoginCode() {
    show({
        title: say('wrong_code', 'Wrong code'),
        text: say('wrong_code_text', 'The code you entered is incorrect.'),
        icon: 'error',
        confirmButtonText: say('try_again', 'Try Again')
    });
}

/** The server turned the sign-in down, and its own words are what shows. */
export function signInRefused({ message, onConfirm } = {}) {
    show({
        text: message,
        icon: 'error',
        confirmButtonText: say('close', 'Close'),
        onConfirm,
        whenMissing: onConfirm
    });
}

/** The registration did not go through at all. */
export function registrationFailed({ message, onConfirm } = {}) {
    show({
        title: say('registration_failed', 'Registration failed'),
        icon: 'error',
        confirmButtonText: say('close', 'Close'),
        onConfirm,
        whenMissing: onConfirm,
        ...body(message || say('registration_failed_text', 'Registration failed.'))
    });
}

/**
 * Held open, with no button, until the request for a code comes back. The gif is
 * preloaded first so the box does not open on an empty frame.
 */
export function sendingCode(then) {
    const image = new Image();
    const open = () => {
        show({
            title: say('sending_email', 'Sending email'),
            text: say('sending_code', 'We are sending your login code.'),
            imageUrl: '/gif/sending-email.gif',
            imageWidth: 80,
            imageHeight: 80,
            showConfirmButton: false,
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();

                // The gif is the spinner; the library's own would be a second one.
                if (Swal.getLoading()) {
                    Swal.getLoading().style.display = 'none';
                }
            },
            whenMissing: () => {}
        });

        then();
    };

    image.onload = open;
    image.onerror = open;
    image.src = '/gif/sending-email.gif';
}

export function codeSent() {
    close();

    show({
        title: say('check_your_email', 'Check your email'),
        text: say('login_code_sent', 'Login code sent. Please check your inbox.'),
        icon: 'info',
        confirmButtonText: say('continue', 'Continue')
    });
}

export function codeNotSent() {
    close();

    show({
        title: say('error', 'Error!'),
        text: say('error_sending_code', 'We could not send the login code. Please try again.'),
        icon: 'error',
        confirmButtonText: say('try_again', 'Try Again')
    });
}

/** Both answers to a code request replace the box that is still spinning. */
function close() {
    if (typeof Swal !== 'undefined') {
        Swal.close();
    }
}
